import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Context,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { guidedFlowById, type GuidedFlowState, type MachineJourneyId } from '@wapharma/domain';
import { unwrapData } from '@wapharma/shared';
import { apiGet } from '../api';
import { getToken } from '../auth';
import {
  hasTourForStep,
  tourForStep,
  tourForFlow,
  toursForPackId,
} from '../tours/definitions';
import {
  isAwaitNone,
  isClickSelectorAwait,
  isSelectorVisibleAwait,
  isTargetClickedAwait,
  resolveResumeIndex,
  resolveStepAwait,
  resolveStepMode,
  type ActiveTour,
  type TourAwait,
  type TourStepDef,
} from '../tours/types';
import { TOURS_ENABLED } from '../tours/flags';
import {
  isElementVisible,
  pathMatchesStep,
  queryTourTarget,
  queryVisible,
  waitForAnyElement,
  waitForElement,
} from '../tours/waitForElement';
import { useJourneyProgress } from '../hooks/useJourneyProgress';
import { openGuideOverlay } from '../lib/guideShell';

type TourContextValue = {
  active: ActiveTour | null;
  currentStep: TourStepDef | null;
  targetRect: DOMRect | null;
  waiting: boolean;
  /** True while org-complete (or equivalent) await is unmet. */
  outcomePending: boolean;
  /** False while step `await` is unmet — gates Terminer / free Suivant. */
  awaitMet: boolean;
  missing: string | null;
  startStepTour: (stepId: string) => boolean;
  startFlowTour: (flowId: string) => boolean;
  startPackTour: (packId: MachineJourneyId, stepIds: string[]) => boolean;
  stopTour: () => void;
  finishTour: () => void;
  next: () => void;
  prev: () => void;
  hasTourForStep: (stepId: string) => boolean;
};

/** Survive Vite HMR — new createContext() breaks Provider/consumer identity mid-session. */
const TourContext: Context<TourContextValue | null> =
  (import.meta.hot?.data.tourCtx as Context<TourContextValue | null> | undefined) ??
  createContext<TourContextValue | null>(null);
if (import.meta.hot) import.meta.hot.data.tourCtx = TourContext;

const NAV_TIMEOUT_MS = 12_000;
const TARGET_TIMEOUT_MS = 15_000;
const OUTCOME_POLL_MS = 4_000;
const AWAIT_POLL_MS = 200;
const AUTO_NEXT_MS = 450;
const TOUR_ACTIVE_ATTR = 'data-wp-tour-active';

async function waitForLayout(): Promise<void> {
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

async function fetchFlowOrgComplete(flowId: string): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  try {
    const raw = await apiGet<{ flows?: GuidedFlowState[] } | { data: { flows?: GuidedFlowState[] } }>(
      '/reports/machine-readiness',
      token,
    );
    const payload = unwrapData(raw) as { flows?: GuidedFlowState[] };
    const flow = payload.flows?.find((f) => f.id === flowId);
    return flow?.status === 'complete';
  } catch {
    return false;
  }
}

function evalSyncAwait(awaitSpec: TourAwait, pathname: string): boolean | null {
  if (awaitSpec === 'none') return true;
  if (awaitSpec === 'org-complete') return null;
  if (isTargetClickedAwait(awaitSpec) || isClickSelectorAwait(awaitSpec)) return false;
  if ('pathMatch' in awaitSpec) {
    try {
      return new RegExp(awaitSpec.pathMatch).test(pathname);
    } catch {
      return false;
    }
  }
  if ('routePrefix' in awaitSpec) {
    return pathname.startsWith(awaitSpec.routePrefix);
  }
  if (isSelectorVisibleAwait(awaitSpec)) {
    return Boolean(queryVisible(awaitSpec.selectorVisible));
  }
  return true;
}

function nodeMatchesSelector(node: EventTarget | null, selector: string): boolean {
  if (!(node instanceof Element)) return false;
  try {
    if (node.matches(selector)) return true;
    if (node.closest(selector)) return true;
  } catch {
    /* invalid selector */
  }
  /* Fallback: Inventaire → Produits link when data-tour missing. */
  if (selector.includes('nav-catalog')) {
    const a = node.closest('a[href]');
    if (a instanceof HTMLAnchorElement && a.closest('[role="menu"]')) {
      const href = a.getAttribute('href') || '';
      if (href === '/catalog' || href.endsWith('/catalog')) return true;
    }
  }
  return false;
}

function stepTargetContains(step: TourStepDef, node: EventTarget | null): boolean {
  if (!(node instanceof Node)) return false;
  const target = queryTourTarget(step);
  if (target && (target === node || target.contains(node))) return true;
  const sels = step.selectors?.length ? step.selectors : step.selector ? [step.selector] : [];
  for (const sel of sels) {
    const el = document.querySelector(sel);
    if (el && (el === node || el.contains(node))) return true;
  }
  return false;
}

function clearTourActiveMarks() {
  document.querySelectorAll(`[${TOUR_ACTIVE_ATTR}]`).forEach((el) => {
    el.removeAttribute(TOUR_ACTIVE_ATTR);
  });
}

function markTourActive(el: Element | null) {
  clearTourActiveMarks();
  if (el instanceof HTMLElement) {
    el.setAttribute(TOUR_ACTIVE_ATTR, '');
  }
}

export function TourProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const { upsert, rows } = useJourneyProgress();
  const [active, setActive] = useState<ActiveTour | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [outcomePending, setOutcomePending] = useState(false);
  const [awaitMet, setAwaitMet] = useState(true);
  const [missing, setMissing] = useState<string | null>(null);
  const navStepKeyRef = useRef<string | null>(null);
  const activeFlowIdRef = useRef<string | null>(null);
  const awaitSatisfiedRef = useRef<Set<string>>(new Set());
  const autoNextLockRef = useRef<string | null>(null);

  const currentStep = active ? (active.steps[active.index] ?? null) : null;
  const stepKey = active ? `${active.tourId}:${active.index}` : null;

  const persistStep = useCallback(
    (flowId: string | null, step: TourStepDef | null, status?: 'STARTED' | 'COMPLETED') => {
      if (!flowId) return;
      void upsert(flowId, {
        status: status ?? 'STARTED',
        lastStepId: step?.id ?? null,
      });
    },
    [upsert],
  );

  const updateRectForStep = useCallback((step: TourStepDef) => {
    const el = queryTourTarget(step);
    if (!el) {
      markTourActive(null);
      setTargetRect(null);
      return false;
    }
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    markTourActive(el);
    setTargetRect(el.getBoundingClientRect());
    return true;
  }, []);

  const stopTour = useCallback(() => {
    clearTourActiveMarks();
    setActive(null);
    setTargetRect(null);
    setWaiting(false);
    setOutcomePending(false);
    setAwaitMet(true);
    setMissing(null);
    navStepKeyRef.current = null;
    activeFlowIdRef.current = null;
    awaitSatisfiedRef.current = new Set();
    autoNextLockRef.current = null;
  }, []);

  const startTour = useCallback(
    (steps: TourStepDef[], tourId: string, flowId: string | null, startIndex = 0) => {
      if (!TOURS_ENABLED) return false;
      if (!steps.length) return false;
      const safeIndex = Math.min(Math.max(0, startIndex), steps.length - 1);
      navStepKeyRef.current = null;
      setMissing(null);
      setTargetRect(null);
      setWaiting(false);
      setOutcomePending(false);
      /* Start unmet so force-click steps do not auto-advance on mount. */
      setAwaitMet(false);
      awaitSatisfiedRef.current = new Set();
      autoNextLockRef.current = null;
      clearTourActiveMarks();
      activeFlowIdRef.current = flowId;
      setActive({ tourId, flowId, steps, index: safeIndex });
      if (flowId && steps[safeIndex]) {
        persistStep(flowId, steps[safeIndex], 'STARTED');
      }
      return true;
    },
    [persistStep],
  );

  const startStepTour = useCallback(
    (stepId: string) => {
      const def = tourForStep(stepId);
      if (!def?.steps.length) return false;
      return startTour(def.steps, def.id, def.flowId ?? null);
    },
    [startTour],
  );

  const startFlowTour = useCallback(
    (flowId: string) => {
      const def = tourForFlow(flowId);
      if (!def?.steps.length) return false;
      const flowDef = guidedFlowById(flowId);
      const revision = flowDef?.revision ?? 1;
      const progress = rows.find((r) => r.flowId === flowId && r.revision === revision);
      let startIndex = 0;
      if (progress?.status === 'STARTED' && progress.lastStepId) {
        startIndex = resolveResumeIndex(def.steps, progress.lastStepId);
      }
      return startTour(def.steps, def.id, flowId, startIndex);
    },
    [rows, startTour],
  );

  const startPackTour = useCallback(
    (packId: MachineJourneyId, stepIds: string[]) => {
      const steps = toursForPackId(packId, stepIds);
      if (!steps.length) return false;
      return startTour(steps, `pack:${packId}`, null);
    },
    [startTour],
  );

  const finishTour = useCallback(() => {
    const flowId = activeFlowIdRef.current;
    const lastStepId = currentStep?.id ?? null;
    const packId = flowId ? guidedFlowById(flowId)?.entryId : undefined;
    stopTour();
    void (async () => {
      if (flowId) {
        try {
          await upsert(flowId, { status: 'COMPLETED', lastStepId });
        } catch {
          /* Still reopen dock — optimistic row may already show done. */
        }
      }
      /* Checklist shares progress store — reopen drawer on next parcours. */
      openGuideOverlay({ packId, refresh: true });
    })();
  }, [currentStep?.id, stopTour, upsert]);

  const next = useCallback(() => {
    setActive((prev) => {
      if (!prev) return null;
      if (prev.index >= prev.steps.length - 1) return prev;
      navStepKeyRef.current = null;
      autoNextLockRef.current = null;
      clearTourActiveMarks();
      const nextIndex = prev.index + 1;
      const nextStep = prev.steps[nextIndex];
      if (prev.flowId && nextStep) persistStep(prev.flowId, nextStep, 'STARTED');
      return { ...prev, index: nextIndex };
    });
    setAwaitMet(false);
    setWaiting(true);
  }, [persistStep]);

  const prev = useCallback(() => {
    setActive((prev) => {
      if (!prev || prev.index <= 0) return prev;
      navStepKeyRef.current = null;
      autoNextLockRef.current = null;
      clearTourActiveMarks();
      const prevIndex = prev.index - 1;
      const prevStep = prev.steps[prevIndex];
      if (prev.flowId && prevStep) persistStep(prev.flowId, prevStep, 'STARTED');
      return { ...prev, index: prevIndex };
    });
    setAwaitMet(false);
    setWaiting(true);
  }, [persistStep]);

  /** Resolve step await (path / selector / click / org-complete). */
  useEffect(() => {
    if (!active || !currentStep || !stepKey) {
      setAwaitMet(true);
      setOutcomePending(false);
      return;
    }

    const awaitSpec = resolveStepAwait(currentStep);
    if (isAwaitNone(awaitSpec)) {
      setAwaitMet(true);
      setOutcomePending(false);
      return;
    }

    if (awaitSatisfiedRef.current.has(currentStep.id)) {
      setAwaitMet(true);
      setOutcomePending(false);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    const cleanups: Array<() => void> = [];

    const markMet = () => {
      if (cancelled) return;
      awaitSatisfiedRef.current.add(currentStep.id);
      setAwaitMet(true);
      setOutcomePending(false);
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      while (cleanups.length) cleanups.pop()?.();
    };

    const checkSync = () => {
      const sync = evalSyncAwait(awaitSpec, loc.pathname);
      if (sync === true) {
        markMet();
        return true;
      }
      if (sync === false) {
        setAwaitMet(false);
        setOutcomePending(false);
        return false;
      }
      return null;
    };

    if (awaitSpec === 'org-complete') {
      const flowId = active.flowId;
      if (!flowId) {
        setAwaitMet(true);
        setOutcomePending(false);
        return;
      }

      const checkOrg = async () => {
        if (document.visibilityState === 'hidden') return;
        const done = await fetchFlowOrgComplete(flowId);
        if (cancelled) return;
        if (done) markMet();
        else {
          setAwaitMet(false);
          setOutcomePending(true);
        }
      };

      setAwaitMet(false);
      setOutcomePending(true);
      void checkOrg();
      timer = setInterval(() => void checkOrg(), OUTCOME_POLL_MS);
      return () => {
        cancelled = true;
        if (timer) clearInterval(timer);
      };
    }

    const immediate = checkSync();
    if (immediate === true) {
      return () => {
        cancelled = true;
      };
    }

    setAwaitMet(false);

    /* pointerdown + click — Pressable delays click; capture still sees both. */
    const onActivate = (e: Event) => {
      if (cancelled) return;
      if (isClickSelectorAwait(awaitSpec)) {
        if (nodeMatchesSelector(e.target, awaitSpec.clickSelector)) markMet();
        return;
      }
      if (isSelectorVisibleAwait(awaitSpec)) {
        if (nodeMatchesSelector(e.target, awaitSpec.selectorVisible)) {
          markMet();
          return;
        }
      }
      if (!stepTargetContains(currentStep, e.target)) return;
      if (isTargetClickedAwait(awaitSpec)) {
        markMet();
        return;
      }
      if (isSelectorVisibleAwait(awaitSpec)) {
        requestAnimationFrame(() => {
          checkSync();
        });
        window.setTimeout(() => checkSync(), 40);
        window.setTimeout(() => checkSync(), 120);
      }
    };
    document.addEventListener('pointerdown', onActivate, true);
    document.addEventListener('click', onActivate, true);
    cleanups.push(() => document.removeEventListener('pointerdown', onActivate, true));
    cleanups.push(() => document.removeEventListener('click', onActivate, true));

    if (isSelectorVisibleAwait(awaitSpec)) {
      const mo = new MutationObserver(() => {
        checkSync();
      });
      mo.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-expanded', 'class', 'data-active'],
      });
      cleanups.push(() => mo.disconnect());
    }

    /* When clickSelector target appears (flyout), also watch — click still required. */
    if (isClickSelectorAwait(awaitSpec)) {
      const mo = new MutationObserver(() => {
        /* Keep hole/spotlight consumers in sync via layout listeners only. */
        void isElementVisible(document.querySelector(awaitSpec.clickSelector));
      });
      mo.observe(document.body, { childList: true, subtree: true });
      cleanups.push(() => mo.disconnect());
    }

    timer = setInterval(() => {
      checkSync();
    }, AWAIT_POLL_MS);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      while (cleanups.length) cleanups.pop()?.();
    };
  }, [active, currentStep, loc.pathname, stepKey]);

  /** Auto-advance when await met on a non-final step. */
  useEffect(() => {
    if (!active || !currentStep || !stepKey) return;
    const awaitSpec = resolveStepAwait(currentStep);
    if (isAwaitNone(awaitSpec)) return;
    if (!awaitMet) return;
    const isLast = active.index >= active.steps.length - 1;
    if (isLast) return;
    /* Lock only when the timer fires — not when scheduling. Otherwise a brief
       awaitMet=true on step start (before the await effect sets false) arms the
       lock, the timeout is cleared, and a later real click never advances. */
    if (autoNextLockRef.current === stepKey) return;
    const t = window.setTimeout(() => {
      autoNextLockRef.current = stepKey;
      next();
    }, AUTO_NEXT_MS);
    return () => window.clearTimeout(t);
  }, [active, awaitMet, currentStep, next, stepKey]);

  useEffect(() => {
    if (!active || !currentStep || !stepKey) return;

    const mode = resolveStepMode(currentStep);
    if (mode === 'instruction' || mode === 'outcome-check') {
      setWaiting(false);
      setMissing(null);
      setTargetRect(null);
      clearTourActiveMarks();
      return;
    }

    let cancelled = false;
    let navTimer: ReturnType<typeof setTimeout> | null = null;

    const fail = (message: string) => {
      if (cancelled) return;
      setMissing(message);
      setWaiting(false);
      setTargetRect(null);
      clearTourActiveMarks();
    };

    const resolveTarget = async () => {
      setWaiting(true);
      setMissing(null);
      setTargetRect(null);
      clearTourActiveMarks();

      /* Optional assist only — force-nav steps omit `route` so user clicks pulsed chrome. */
      if (
        currentStep.route &&
        !pathMatchesStep(loc.pathname, currentStep.route, {
          exact: currentStep.routeExact === true,
        })
      ) {
        if (navStepKeyRef.current !== stepKey) {
          navStepKeyRef.current = stepKey;
          navigate(currentStep.route);
        }
        /* Always arm watchdog — navigate to same URL may not remount effect. */
        navTimer = setTimeout(() => {
          fail('Page inaccessible. Quittez le guide.');
        }, NAV_TIMEOUT_MS);
        return;
      }

      if (navTimer) clearTimeout(navTimer);

      const hasTarget = Boolean(currentStep.selector || currentStep.selectors?.length);
      if (!hasTarget) {
        setWaiting(false);
        return;
      }

      try {
        if (currentStep.selectors?.length) {
          await waitForAnyElement(currentStep.selectors, TARGET_TIMEOUT_MS);
        } else if (currentStep.selector) {
          await waitForElement(currentStep.selector, TARGET_TIMEOUT_MS);
        }
        if (cancelled) return;
        await waitForLayout();
        if (cancelled) return;
        if (!updateRectForStep(currentStep)) {
          fail('Élément introuvable. Quittez ou continuez manuellement.');
          return;
        }
        setWaiting(false);
        if (currentStep.autoOpenNavCluster) {
          const el = queryTourTarget(currentStep);
          const anchor = el?.getAttribute('data-tour') || 'nav-inventaire';
          const fire = () =>
            window.dispatchEvent(
              new CustomEvent('wp-tour:open-nav-cluster', {
                detail: { tourAnchor: anchor },
              }),
            );
          fire();
          window.setTimeout(fire, 80);
          window.setTimeout(fire, 200);
        }
        if (currentStep.autoOpenCreateMenu) {
          const fire = () => window.dispatchEvent(new CustomEvent('wp-tour:open-create-menu'));
          fire();
          window.setTimeout(fire, 80);
          window.setTimeout(fire, 200);
        }
      } catch {
        fail('Élément introuvable. Quittez ou continuez manuellement.');
      }
    };

    void resolveTarget();

    return () => {
      cancelled = true;
      if (navTimer) clearTimeout(navTimer);
    };
  }, [active, currentStep, loc.pathname, navigate, stepKey, updateRectForStep]);

  useEffect(() => {
    if (!active || !currentStep || waiting || !targetRect) return;
    if (!currentStep.selector && !currentStep.selectors?.length) return;

    const onLayout = () => updateRectForStep(currentStep);
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [active, currentStep, targetRect, updateRectForStep, waiting]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stopTour();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, stopTour]);

  const value = useMemo<TourContextValue>(
    () => ({
      active,
      currentStep,
      targetRect,
      waiting,
      outcomePending,
      awaitMet,
      missing,
      startStepTour,
      startFlowTour,
      startPackTour,
      stopTour,
      finishTour,
      next,
      prev,
      hasTourForStep,
    }),
    [
      active,
      currentStep,
      targetRect,
      waiting,
      outcomePending,
      awaitMet,
      missing,
      startStepTour,
      startFlowTour,
      startPackTour,
      stopTour,
      finishTour,
      next,
      prev,
    ],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
}
